<script lang="ts" setup>
import {cx} from 'class-variance-authority'
import {useLogistics} from '~/composables/cms/logistics'

import ReviewsSmall from '~/components/workshop/reviews-small.vue'
import Solutions from '~/components/logistics/solutions/solutions.vue'
import SolutionsCard from '~/components/logistics/solutions/solutions-card.vue'
import ShadowBorder from '~/components/managed-projects/shadow-border.vue'
import SingleTestimonial from '~/components/managed-projects/single-testimonial.vue'
import BCard from '~/components/logistics/b-card.vue'
import OurProjects from '~/components/workshop/our-projects.vue'

const {
  public: {baseUrl},
} = useRuntimeConfig()

const lp = useLocalePath()

const {fetch} = useLogistics()
const {data: initialData} = await useAsyncData('logistics', () => fetch())
const {data: logistics} = useLivePreview({initialData: initialData.value, depth: 3})

if (!logistics.value) {
  throw createError({status: 503, message: 'Could not load the page.'})
}

const pixelGridLg = [
  '  111111111111111111111111111111111111111111111   ',
  ' 1111111111111111     111111211111111111111111    ',
  '  112221111111122111111111221211111111111111111   ',
  '       211111111111111111111111111111    2111111  ',
  '       211111111111111111111111111111    2111111  ',
  '                                    1    2111111  ',
  '       2111111                     11    2111111  ',
  '1111                              111 221111111   ',
  '1111111111                            11 221111111',
  '1111111111                                        ',
  '                                    2111 221111111',
  '                                   22111 221111111',
  '                                  122111 221111111',
  '                                      11 221111111',
].join('\n')

const seo = logistics.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/logistics`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const gradientClass = 'text-transparent bg-clip-text leading-tight bg-gradient-to-l from-gray-950 to-gray-400'
</script>

<template>
  <main v-if="logistics" class="flex w-full flex-col overflow-hidden">
    <Container
      class="screen-content relative flex flex-col items-center space-y-6 pb-16 pt-32 md:pb-16 lg:pb-16 lg:pt-48"
    >
      <ReviewsSmall class="py-0 text-center lg:text-left" />

      <Heading size="hero" as="h1" class="w-full text-center xs:mb-8 lg:mb-4">
        <span :class="cx(gradientClass, 'from-gray-950 to-gray-600 pr-2')">{{ $t('logistics.title') }}</span>
        <br />
        <span :class="cx(gradientClass, 'from-gray-950 to-gray-600')">{{ $t('logistics.sub_title') }}</span>
      </Heading>

      <Paragraph
        v-if="logistics.hero?.body"
        class="mb-8 text-center text-sm text-gray-600 xs:px-12 xs:text-center lg:w-2/4 lg:px-0"
        size="medium"
      >
        {{ logistics.hero.body }}
      </Paragraph>

      <div class="flex flex-col sm:justify-center lg:justify-start">
        <div class="flex items-center gap-2">
          <Action intent="main" as="link" to="/contact" size="normal">
            {{ $t('logistics.cta', 'Contact us') }}
          </Action>

          <Action intent="normal" as="link" size="normal" to="/logistics/custom-warehouse-management-system">
            {{ $t('logistics.hero_button', 'View solutions') }}
          </Action>
        </div>
      </div>

      <div v-if="logistics.testimonials?.partners?.length" class="mt-14">
        <Partners :title="false" :partners="logistics.testimonials.partners" class="mt-28 px-8 pb-4 lg:px-0" />
      </div>

      <Container id="solutions" class="screen-content mx-auto flex flex-col items-center border-t border-gray-200">
        <ShadowBorder class="pb-16" position="top" />
        <Solutions v-if="logistics.solutions" :data="logistics.solutions" />
      </Container>

      <Container v-if="logistics.solutionCards?.length" class="screen-content px-2 lg:py-8">
        <SolutionsCard :cards="logistics.solutionCards" />
      </Container>

      <Container class="screen-content mx-auto flex flex-col items-center">
        <div
          class="flex w-full flex-col items-start justify-between space-y-4 lg:flex-row lg:items-center lg:space-y-0"
        >
          <div class="text-left">
            <Heading as="h2" size="caption">
              {{ logistics.solutionsContact?.eyebrow }}
            </Heading>
            <Heading as="h3" size="medium">
              {{ logistics.solutionsContact?.title }}
            </Heading>
            <Paragraph as="p">
              {{ logistics.solutionsContact?.description }}
            </Paragraph>
          </div>

          <Action
            v-if="logistics.solutionsContact.action?.label"
            :intent="logistics.solutionsContact?.action?.intent"
            :size="logistics.solutionsContact.action.size"
            :to="logistics.solutionsContact?.action?.href ?? undefined"
          >
            {{ logistics.solutionsContact?.action?.label }}
          </Action>
        </div>

        <div class="pointer-events-none absolute mx-auto mt-20 h-full w-full max-w-xl">
          <div
            class="absolute bottom-0 left-0 h-full w-full origin-bottom scale-[3] bg-radial-gradient-main md:scale-100 md:scale-x-[4]"
          />
          <div class="absolute bottom-0 left-0 h-px w-full scale-x-150 bg-gradient-main md:scale-x-100" />
        </div>
      </Container>

      <Container class="mx-auto mt-8 max-w-screen-xl px-8 py-20">
        <section class="mx-auto mb-24 flex flex-col items-center text-center">
          <Heading as="h2" size="caption">{{ logistics.benefitsSection?.eyebrow }}</Heading>
          <Heading class="w-full lg:w-1/2" as="h3" size="large">{{ logistics.benefitsSection?.title }}</Heading>

          <Paragraph class="mt-4 w-full lg:w-1/2" as="p">{{ logistics.benefitsSection?.description }}</Paragraph>
        </section>

        <div class="px-0! mt-16 grid grid-cols-1 justify-center md:grid-cols-3">
          <BCard v-for="(card, index) in logistics.benefitsSection?.benefits" :key="index" :data="card" />
        </div>
      </Container>

      <SingleTestimonial
        v-if="logistics.singleTestimonial"
        :large="false"
        class="border-b-0 py-14 lg:py-24"
        :data="logistics.singleTestimonial"
      />

      <OurProjects v-if="logistics.projects" :data="logistics.projects" />

      <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
        <PixelGrid class="h-full" :pattern="pixelGridLg" />
      </div>
    </Container>

    <Container
      v-if="logistics.faq"
      class="screen-content flex flex-col items-center py-16 text-center md:pb-16 lg:pb-10 lg:pt-16"
    >
      <Heading size="caption" as="h2">{{ logistics.faq.headline }}</Heading>
      <Heading as="h3" size="large" class="w-full lg:w-1/2">{{ logistics.faq.title }}</Heading>
      <Paragraph class="mt-2 w-full pb-6 lg:w-1/2">{{ logistics.faq.body }}</Paragraph>

      <Accordion
        v-if="logistics.faq.questions"
        class="mt-8 pb-16 text-sm xs:px-12 xl:px-72"
        :items="logistics.faq.questions"
      />
    </Container>

    <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
  </main>
</template>
